<template>
  <div
    v-for="section in sections"
    :key="section.name"
    class="border-t border-fp-green-7/20 first:border-t-0 -mx-2"
  >
    <div :class="isSectionOpen(section.sectionType) ? 'bg-fp-green-7/20' : ''">
      <button
        type="button"
        class="group flex w-full items-center justify-between gap-2 px-2 py-2.5 text-left text-fp-green-1 transition-colors hover:bg-[#1b332b]/50"
        :aria-expanded="isSectionOpen(section.sectionType)"
        @click="toggleSection(section.sectionType)"
      >
        <span class="flex min-w-0 flex-1 items-center gap-2.5">
          <span
            class="flex h-6 w-6 shrink-0 items-center justify-center"
            :class="section.icon ? 'rounded bg-fp-green-7 text-fp-green-0' : ''"
            aria-hidden="true"
          >
            <component
              :is="section.icon"
              v-if="section.icon"
              class="h-3.5 w-3.5"
            />
          </span>
          <span class="truncate text-sm tracking-tight text-fp-green-0">{{
            section.name
          }}</span>
        </span>
        <ChevronUpIcon
          v-if="isSectionOpen(section.sectionType)"
          class="h-5 w-5"
        />
        <ChevronDownIcon v-else class="h-5 w-5" />
      </button>
      <div v-if="isSectionOpen(section.sectionType)" class="flex flex-col px-3">
        <NavigationLink
          v-for="item in section.items"
          :key="item.name"
          :item="item"
          :small="small"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import {
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
} from '@heroicons/vue/24/outline'
import NavigationLink from '~/components/navigation/NavigationLink.vue'
import type { SidebarSection } from '~/models/menu-item'
import { MENU_SECTION } from '~/models/menu-item'
import { useUserSessionStore } from '~~/store/user-session'
import { useAdminNavigationMenu } from '~/composables/useAdminNavigationMenu'
import WalletIcon from '../icons/streamline/WalletIcon.vue'
import MoneySharingGiveIcon from '~/../member-webapp-shared/components/icons/streamline/MoneySharingGiveIcon.vue'
import CheckBadgeSolidIcon from '~/../member-webapp-shared/components/icons/streamline/CheckBadgeSolidIcon.vue'
import PenWritingCalligraphyIcon from '~/../member-webapp-shared/components/icons/streamline/PenWritingCalligraphyIcon.vue'
import SmartphoneIcon from '~/../member-webapp-shared/components/icons/streamline/SmartphoneIcon.vue'
import UserIncreaseIcon from '~/../member-webapp-shared/components/icons/streamline/UserIncreaseIcon.vue'

const userSessionStore = useUserSessionStore()
const { getData } = userSessionStore

// This is called using useAsyncData so that this data is populated on
// the server (SSR), i.e. blocks navigation until this is resolved.
await useAsyncData(
  'getUserSessionData',
  // Make sure to return a value. This will allow Nuxt to skip running the action on the client side and reuse the value from the server.
  async () => await getData().then(() => true)
)

const { menu } = useAdminNavigationMenu()

const openMenuSections = ref<MENU_SECTION[]>([])

const isSectionOpen = (sectionType: MENU_SECTION) =>
  openMenuSections.value.includes(sectionType)

const sections = computed((): SidebarSection[] => {
  return [
    {
      name: 'Granting',
      items: menu.value.filter(
        (item) => item.sectionType === MENU_SECTION.granting
      ),
      sectionType: MENU_SECTION.granting,
      icon: MoneySharingGiveIcon,
    },
    {
      name: 'DAF Management',
      items: menu.value.filter(
        (item) => item.sectionType === MENU_SECTION.dafManagement
      ),
      sectionType: MENU_SECTION.dafManagement,
      icon: WalletIcon,
    },
    {
      name: 'App Content',
      items: menu.value.filter(
        (item) => item.sectionType === MENU_SECTION.appContent
      ),
      sectionType: MENU_SECTION.appContent,
      icon: PenWritingCalligraphyIcon,
    },
    {
      name: 'HIFO',
      items: menu.value.filter(
        (item) => item.sectionType === MENU_SECTION.hifo
      ),
      sectionType: MENU_SECTION.hifo,
      icon: CheckBadgeSolidIcon,
    },
    {
      name: 'Admin',
      items: menu.value.filter(
        (item) => item.sectionType === MENU_SECTION.admin
      ),
      sectionType: MENU_SECTION.admin,
      icon: SmartphoneIcon,
    },
    {
      name: 'Growth',
      items: menu.value.filter(
        (item) => item.sectionType === MENU_SECTION.growth
      ),
      sectionType: MENU_SECTION.growth,
      icon: UserIncreaseIcon,
    },
    {
      name: 'Training',
      items: menu.value.filter(
        (item) => item.sectionType === MENU_SECTION.training
      ),
      sectionType: MENU_SECTION.training,
      icon: ShieldCheckIcon,
    },
    {
      name: 'Assets',
      items: menu.value.filter(
        (item) => item.sectionType === MENU_SECTION.assets
      ),
      sectionType: MENU_SECTION.assets,
      icon: ComputerDesktopIcon,
    },
    {
      name: 'Contracts',
      items: menu.value.filter(
        (item) => item.sectionType === MENU_SECTION.contracts
      ),
      sectionType: MENU_SECTION.contracts,
      icon: DocumentTextIcon,
    },
    {
      name: 'Events',
      items: menu.value.filter(
        (item) => item.sectionType === MENU_SECTION.events
      ),
      sectionType: MENU_SECTION.events,
      icon: CalendarDaysIcon,
    },
    {
      name: 'Other',
      items: menu.value.filter(
        (item) => item.sectionType === MENU_SECTION.other
      ),
      sectionType: MENU_SECTION.other,
    },
  ].filter((section) => section.items.length > 0)
})

defineProps({
  small: {
    type: Boolean,
    default: false,
  },
})

const toggleSection = (section: MENU_SECTION) => {
  if (openMenuSections.value.includes(section)) {
    openMenuSections.value = openMenuSections.value.filter((x) => x !== section)
  } else {
    openMenuSections.value.push(section)
  }
}
</script>
